/**
 * Review Page (Student)
 * Shows stories and conversations with activities due for spaced repetition review.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { RotateCcw } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Icon } from '@/components/atoms/Icon';
import { Button } from '@/components/atoms/Button';
import { Spinner } from '@/components/atoms/Spinner';
import { LearningCard } from '@/components/molecules/LearningCard';
import { ViewToggle, type ViewMode } from '@/components/atoms/ViewToggle';
import { usersAPI, type ReviewDueSource } from '@/services/api/users.api';
import { buildRoute, ROUTES } from '@/constants/routes';
import { usePageTransitionEntrance } from '@/context/PageTransitionEntranceContext';
import { entranceAwareBlockTransition } from '@/constants/storyResourcesEntrance';
import styles from './ReviewPage.module.scss';

const EASE: [number, number, number, number] = [0.42, 0, 0.58, 1];
const PAGE_SIZE = 9;

export function ReviewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const pageReady = usePageTransitionEntrance();
  const reducedMotion = Boolean(useReducedMotion());

  const navState = location.state as {
    returnFromReview?: boolean;
    scrollToReviewSourceType?: 'story' | 'conversation';
    scrollToReviewSourceId?: string;
    scrollY?: number;
    viewMode?: ViewMode;
    reviewDueLoadedCount?: number;
    reviewDueHasMore?: boolean;
  };

  const [sources, setSources] = useState<ReviewDueSource[]>([]);
  const [totalSources, setTotalSources] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(navState?.viewMode ?? 'card');

  const initialLoadCountRef = useRef<number | null>(null);
  const [loadMoreVisible, setLoadMoreVisible] = useState(false);

  const hidden = { opacity: 0, y: 20 };
  const visible = { opacity: 1, y: 0 };
  const fadeUp = (delay: number) => ({
    initial: hidden,
    animate: pageReady ? visible : hidden,
    transition: entranceAwareBlockTransition(pageReady, reducedMotion, 0.42, delay),
  });

  useEffect(() => {
    const st = location.state as {
      returnFromReview?: boolean;
      reviewDueLoadedCount?: number;
      reviewDueHasMore?: boolean;
    } | null;
    const loaded = st?.reviewDueLoadedCount;
    const restoreLimit =
      st?.returnFromReview && typeof loaded === 'number' && loaded >= PAGE_SIZE ? loaded : null;

    setIsLoading(true);
    setError(false);
    const req =
      restoreLimit != null
        ? usersAPI.getReviewDue(restoreLimit, 0)
        : usersAPI.getReviewDue(PAGE_SIZE, 0);
    req
      .then((data) => {
        setSources(data.sources);
        setTotalSources(data.totalSources);
        setHasMore(st?.reviewDueHasMore ?? data.hasMore);
      })
      .catch(() => setError(true))
      .finally(() => setIsLoading(false));
    // Solo []: al volver de la historia/recurso el componente se remonta; no enlazar location.state
    // para que el replace tras restaurar scroll no dispare otra carga.
  {/* eslint-disable-next-line react-hooks/exhaustive-deps */}
  }, []);

  useEffect(() => {
    if (!isLoading && sources.length > 0 && initialLoadCountRef.current === null) {
      initialLoadCountRef.current = sources.length;
    }
  }, [isLoading, sources.length]);

  useEffect(() => {
    setLoadMoreVisible(false);
  }, [sources.length]);

  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;
    try {
      setIsLoadingMore(true);
      const data = await usersAPI.getReviewDue(PAGE_SIZE, sources.length);
      setSources((prev) => [...prev, ...data.sources]);
      setHasMore(data.hasMore);
      setTotalSources(data.totalSources);
    } catch {
      setHasMore(false);
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasMore, isLoadingMore, sources.length]);

  const scrollToType = navState?.scrollToReviewSourceType;
  const scrollToId = navState?.scrollToReviewSourceId;
  const savedScrollY = navState?.scrollY;
  const scrollRestoredRef = useRef(false);
  const isGridReady = !isLoading && !error && totalSources > 0 && sources.length > 0;

  useEffect(() => {
    if (!scrollToType || !scrollToId || scrollRestoredRef.current || !isGridReady) return;
    const timer = setTimeout(() => {
      scrollRestoredRef.current = true;
      if (savedScrollY != null) {
        window.scrollTo(0, savedScrollY);
      } else {
        const el = document.getElementById(`review-card-${scrollToType}-${scrollToId}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      navigate(ROUTES.STUDENT.REVIEW, { replace: true, state: { viewMode, returnFromReview: true } });
    }, 100);
    return () => clearTimeout(timer);
  }, [scrollToType, scrollToId, savedScrollY, isGridReady, navigate, viewMode]);

  const handleReview = (source: ReviewDueSource) => {
    const common = {
      fromReview: true as const,
      reviewDueLoadedCount: sources.length,
      reviewDueHasMore: hasMore,
      scrollY: window.scrollY,
      viewMode,
    };
    if (source.sourceType === 'story') {
      navigate(buildRoute(ROUTES.STUDENT.STORY, { id: source.sourceId }), { state: common });
    } else {
      navigate(buildRoute(ROUTES.STUDENT.CONVERSATION, { id: source.sourceId }), { state: common });
    }
  };

  const tagForSource = (source: ReviewDueSource) => {
    if (source.sourceType === 'story') {
      return source.sourceTag || t('common.general');
    }
    return t('conversationsPage.conversationTag');
  };

  return (
    <div className={styles.reviewPage}>
      <motion.header className={styles.reviewPage__header} {...fadeUp(0)}>
        <h1 className={styles.reviewPage__title}>{t('reviewPage.title')}</h1>
        <p className={styles.reviewPage__subtitle}>{t('reviewPage.subtitle')}</p>
      </motion.header>

      {isLoading ? (
        <motion.div className={styles.reviewPage__loading} {...fadeUp(0.18)}>
          <Spinner size="lg" aria-label={t('common.loading')} />
        </motion.div>
      ) : error ? (
        <motion.p className={styles.reviewPage__error} {...fadeUp(0.18)}>
          {t('reviewPage.error')}
        </motion.p>
      ) : totalSources === 0 ? (
        <motion.div className={styles.reviewPage__empty} {...fadeUp(0.18)}>
          <Icon icon={RotateCcw} size="lg" />
          <p className={styles.reviewPage__emptyTitle}>{t('reviewPage.emptyTitle')}</p>
          <p className={styles.reviewPage__emptyText}>{t('reviewPage.emptyText')}</p>
        </motion.div>
      ) : (
        <div className={styles.reviewPage__section}>
          <motion.div className={styles.reviewPage__viewToggleRow} {...fadeUp(0.34)}>
            <span className={styles.reviewPage__viewToggleLabel}>
              {t('reviewPage.sourcesDue', { count: totalSources })}
            </span>
            <ViewToggle value={viewMode} onValueChange={setViewMode} aria-label={t('reviewPage.viewModeAria')} />
          </motion.div>

          <div className={styles.reviewPage__gridBlock}>
            <div className={cn(styles.reviewPage__grid, viewMode === 'list' && styles['reviewPage__grid--list'])}>
              {sources.map((source, i) => {
                const isInitialCard = initialLoadCountRef.current === null || i < initialLoadCountRef.current;
                const baseDelay = isInitialCard ? 0.5 : 0;
                const cardDelay = baseDelay + Math.min(i * 0.025, 0.15);
                const isLast = i === sources.length - 1;
                return (
                  <motion.div
                    key={`${source.sourceType}:${source.sourceId}`}
                    id={`review-card-${source.sourceType}-${source.sourceId}`}
                    initial={!reducedMotion ? { opacity: 0, y: 20, scale: 0.95 } : false}
                    whileInView={pageReady && !reducedMotion ? { opacity: 1, y: 0, scale: 1 } : undefined}
                    viewport={{ once: true, margin: '-40px' }}
                    transition={{ duration: 0.42, delay: cardDelay, ease: EASE }}
                    onAnimationComplete={isLast ? () => setLoadMoreVisible(true) : undefined}
                  >
                    <LearningCard
                      use="story"
                      style="wide"
                      compact={viewMode === 'list'}
                      title={source.sourceTitle}
                      description={source.sourceDescription ?? ''}
                      tag={tagForSource(source)}
                      xpReward={source.sourceXpReward}
                      estimatedTime={source.sourceEstimatedTime}
                      backgroundImage={source.sourceBackgroundImage ?? undefined}
                      completed={false}
                      onClick={() => handleReview(source)}
                    />
                  </motion.div>
                );
              })}
            </div>

            {hasMore && (
              <motion.div
                key={sources.length}
                className={styles.reviewPage__loadMore}
                initial={!reducedMotion ? { opacity: 0, y: 16 } : false}
                animate={!reducedMotion ? (loadMoreVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }) : undefined}
                transition={{ duration: 0.38, ease: EASE }}
              >
                <Button variant="primary" onClick={handleLoadMore} disabled={isLoadingMore}>
                  {isLoadingMore ? t('common.loading') : t('storiesPage.loadMore')}
                </Button>
              </motion.div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ReviewPage;
